import React, { useEffect, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import LocalTaxiRoundedIcon from "@mui/icons-material/LocalTaxiRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import backgroundImage from "../assets/img/background.jpg";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { calcEst, showEst, optionsRequired, FONT_FAMILY } from "../common/sharedFunctions";

const Hero = ({ role }) => {
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const { t } = useTranslation();
  const [loggedIn, setLoggedIn] = useState(false);

  useEffect(() => setLoggedIn(Boolean(auth.profile && auth.profile.uid)), [auth.profile]);

  const openBooking = () => {
    if (!loggedIn) {
      navigate("/login");
      return;
    }
    navigate(
      role === "driver" ||
        (calcEst && !showEst && !optionsRequired && (role === "admin" || role === "fleetadmin"))
        ? "/bookings"
        : "/addbookings",
    );
  };

  const steps = [
    { icon: <PlaceRoundedIcon />, title: t("landing_step_where_title"), text: t("landing_step_where_text") },
    { icon: <LocalTaxiRoundedIcon />, title: t("landing_step_ride_title"), text: t("landing_step_ride_text") },
    { icon: <CheckCircleRoundedIcon />, title: t("landing_step_go_title"), text: t("landing_step_go_text") },
  ];

  return (
    <Box component="section" sx={{
      minHeight: { xs: 760, md: 720 }, display: "flex", alignItems: "center",
      backgroundImage: `linear-gradient(90deg, rgba(5,15,35,.94) 0%, rgba(5,15,35,.72) 54%, rgba(5,15,35,.38) 100%), url(${backgroundImage})`,
      backgroundSize: "cover", backgroundPosition: "center", px: { xs: 2.5, sm: 4, md: 6 },
      pt: { xs: 14, md: 16 }, pb: { xs: 8, md: 10 }, boxSizing: "border-box",
    }}>
      <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center" sx={{ width: "100%", maxWidth: 1240, mx: "auto" }}>
        <Grid item xs={12} md={7}>
          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 1.5, py: .75, mb: 3, borderRadius: 99, bgcolor: "rgba(255,255,255,.1)", border: "1px solid rgba(255,255,255,.18)", color: "white" }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#FFD43B" }} />
            <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 13, fontWeight: 700 }}>{t("landing_proudly_serving")}</Typography>
          </Box>
          <Typography component="h1" sx={{ color: "white", fontFamily: FONT_FAMILY, fontSize: { xs: "2.8rem", sm: "4rem", md: "4.75rem" }, fontWeight: 800, letterSpacing: "-.055em", lineHeight: .98, maxWidth: 720 }}>
            {t("landing_hero_title")}
          </Typography>
          <Typography sx={{ mt: 3, color: "rgba(255,255,255,.78)", fontFamily: FONT_FAMILY, fontSize: { xs: 17, md: 20 }, lineHeight: 1.6, maxWidth: 580 }}>
            {t("landing_hero_subtitle")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mt: 4 }}>
            <Button onClick={openBooking} variant="contained" sx={{ bgcolor: "#FFD43B", color: "#081224", px: 3.5, py: 1.6, borderRadius: 3, fontFamily: FONT_FAMILY, fontSize: 15, fontWeight: 800, textTransform: "none", boxShadow: "0 12px 30px rgba(255,212,59,.22)", "&:hover": { bgcolor: "#FFE16E", transform: "translateY(-2px)" } }}>{t("landing_book_ride")}</Button>
            <Button onClick={() => navigate("/about-us")} variant="outlined" sx={{ color: "white", borderColor: "rgba(255,255,255,.38)", px: 3, py: 1.6, borderRadius: 3, fontFamily: FONT_FAMILY, fontWeight: 700, textTransform: "none", "&:hover": { borderColor: "white", bgcolor: "rgba(255,255,255,.08)" } }}>{t("landing_discover")}</Button>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 2, sm: 3 }, mt: 4.5, color: "rgba(255,255,255,.7)" }}>
            {[t("landing_fast_booking"), t("landing_local_drivers"), t("landing_secure_rides")].map((label) => (
              <Typography key={label} sx={{ display: "flex", alignItems: "center", gap: .75, fontFamily: FONT_FAMILY, fontSize: 13, fontWeight: 600 }}><CheckCircleRoundedIcon sx={{ color: "#FFD43B", fontSize: 18 }} /> {label}</Typography>
            ))}
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <Box sx={{ bgcolor: "rgba(255,255,255,.96)", borderRadius: { xs: 4, md: 5 }, p: { xs: 2.5, sm: 3.5 }, boxShadow: "0 30px 80px rgba(0,0,0,.32)", backdropFilter: "blur(16px)" }}>
            <Typography sx={{ fontFamily: FONT_FAMILY, color: "#0B1739", fontWeight: 800, fontSize: 23, mb: .75 }}>{t("landing_simple_title")}</Typography>
            <Typography sx={{ fontFamily: FONT_FAMILY, color: "#667085", fontSize: 14, mb: 2.5 }}>{t("landing_simple_subtitle")}</Typography>
            {steps.map((step, index) => (
              <Box key={step.title} sx={{ display: "flex", gap: 2, py: 2, borderTop: index ? "1px solid #E8ECF2" : "none" }}>
                <Box sx={{ width: 44, height: 44, flex: "0 0 auto", display: "grid", placeItems: "center", borderRadius: 3, bgcolor: index === 1 ? "#FFD43B" : "#EEF2FA", color: "#0B1739", "& svg": { fontSize: 23 } }}>{step.icon}</Box>
                <Box><Typography sx={{ fontFamily: FONT_FAMILY, color: "#101828", fontWeight: 750, fontSize: 15 }}>{step.title}</Typography><Typography sx={{ mt: .4, fontFamily: FONT_FAMILY, color: "#667085", fontSize: 13.5, lineHeight: 1.45 }}>{step.text}</Typography></Box>
              </Box>
            ))}
            <Button
              onClick={openBooking}
              variant="contained"
              fullWidth
              startIcon={<LocalTaxiRoundedIcon />}
              sx={{
                mt: 2,
                py: 1.45,
                bgcolor: "#3867E8",
                color: "white",
                borderRadius: 3,
                fontFamily: FONT_FAMILY,
                fontSize: 15,
                fontWeight: 800,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { bgcolor: "#244FC8", boxShadow: "none" },
              }}
            >
              {loggedIn ? t("landing_start_booking") : t("landing_signin_booking")}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Hero;
